import React, { useCallback, useEffect, useMemo, useState } from "react";
// eslint-disable-next-line import/no-unresolved
import Notification from "pages/_components/Notification";
import Head from "pages/_components/Head";
import { Text, DebitDirectDetail, Box, Button } from "@ui-kit/components/index.es";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";
import { resizableRoute } from "pages/_components/Resizable";
import { selectors as sessionSelectors } from "reducers/session";
import { actions as directDebitActions, selectors as directDebitSelectors } from "reducers/directDebit";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import NewDirectDebitSummaryData from "pages/v2/directDebit/NewDirectDebitSummaryData";
import DownloadButton from "pages/_components/DownloadButton";
import * as accountUtils from "util/accounts";
import * as i18n from "util/i18n";
import PropTypes from "prop-types";
import moment from "moment";
import InfoTag from "pages/_components/InfoTag";
import { actions as formActions } from "reducers/form";
import { useEmbeddedV3ShellLayoutSync } from "util/v3ShellLayoutSync";

const ROW_PER_PAGE = 10;

const DetailDirectDebit = (props) => {
    const [accountName, setAccountName] = useState("");
    const [directDebitSelected, setDirectDebitSelected] = useState({});
    const [dataDetails, setDataDetails] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [pageNumber, setPageNumber] = useState(0);

    const {
        isDesktop,
        dispatch,
        directDebitList,
        loggedUser,
        accounts,
        match,
        fetchingDownload,
        embeddedInV3Shell,
    } = props;
    const getStatus = (state, id) => {
        const listIdErrors = directDebitSelected.listIdErrors || [];
        switch (state) {
            case "FINISHED":
                return "PENDING";
            case "PENDING":
                return "PENDING";
            case "PROCESSED_WITH_SUCCESS":
                return "FINISHED";
            case "PROCESSED_WITH_ERRORS":
                if (listIdErrors.includes(Number(id))) {
                    return "FAILED";
                }
                return "FINISHED";
            case "CANCELLED":
                return "CANCELLED";
            case "PROCESSING":
                return "PENDING";
            default:
                return "PENDING";
        }
    };

    useEffect(() => {
        if (!directDebitList?.length > 0) {
            dispatch(push("/v2/directDebit/list"));
        } else {
            const { idTransaction } = match.params;
            const directDebitCurrent = directDebitList.find(
                (directDebit) => directDebit.id.toString() === idTransaction,
            );
            setDirectDebitSelected(directDebitCurrent || {});
        }
        if (!accounts?.length > 0) {
            dispatch(accountsActions.listAccounts(loggedUser?.userId));
        }
    }, []);
    const isRetried = (detail, loteStatus) => {
        const retryableStatuses = ["PROCESSED_WITH_SUCCESS", "PROCESSED_WITH_ERRORS"];
        const terminalOrderStatuses = ["FAILED", "FINISHED"];
        const orderStatus = getStatus(loteStatus, detail?.id);
        const retries = Number(detail?.retryCount);
        return (
            retryableStatuses.includes(loteStatus) &&
            terminalOrderStatuses.includes(orderStatus) &&
            !Number.isNaN(retries) &&
            retries > 0
        );
    };

    useEffect(() => {
        if (directDebitSelected?.transaction?.data?.file?.details) {
            const details = directDebitSelected?.transaction?.data?.file?.details;
            const loteStatus = directDebitSelected.status;
            const detailsWithStatus = details.map((detail) => ({
                ...detail,
                status: getStatus(loteStatus, detail?.id),
                retryInfo: isRetried(detail, loteStatus) ? i18n.get("directDebit.detail.order.retryInfo") : null,
            }));
            setDataDetails(detailsWithStatus);
            setPageNumber(1);
            setTotalPages(
                Math.ceil((directDebitSelected?.transaction?.data?.file?.details?.length || 0) / ROW_PER_PAGE),
            );
        }
    }, [directDebitSelected?.transaction]);

    useEffect(() => {
        if (accounts.length > 0) {
            const newAccountName = accountUtils.getAccountName(
                accounts.find((acc) => acc.cbu === directDebitSelected.accountNumberId),
            );
            setAccountName(newAccountName);
        }
    }, [accounts, directDebitSelected]);

    const handleBack = () => dispatch(push("/v2/directDebit/list"));

    const handleDownload = (format) => {
        dispatch(
            directDebitActions.downloadDirectDebitDetailFile(format, directDebitSelected?.transaction?.idTransaction),
        );
    };

    const cancelTransaction = useCallback(
        (transaction) => {
            const cancelTransactionData = <NewDirectDebitSummaryData isDesktop={isDesktop} />;
            dispatch(push({ pathname: "/transactions/cancel" }));
            dispatch(
                formActions.cancelTransactionData({
                    idTransaction: transaction.idTransaction,
                    content: cancelTransactionData,
                    title: i18n.get("directDebit.success.title"),
                    force: true,
                }),
            );

            dispatch(directDebitActions.directDebitTransactionSetData(transaction));
            dispatch(directDebitActions.setGoBackDirectDebit(true));
            dispatch(formActions.cancelTransactionPre({ idActivity: transaction.idActivity, idForm: "" }));
        },
        [dispatch, isDesktop],
    );

    const detailShellRightContentMobile = useMemo(() => {
        if (!embeddedInV3Shell) {
            return null;
        }
        const trx = directDebitSelected?.transaction;
        const st = trx?.idTransactionStatus;
        return (
            <Box justify="end" align="center" css={{ flexWrap: "wrap", gap: 8 }}>
                {["PENDING", "PROCESSING"].includes(st) && (
                    <Button
                        icon="Error"
                        iconVariant="outline"
                        variant="solid"
                        onClick={() => cancelTransaction(trx)}
                        isInactive={false}
                        loading={false}
                        aria-label={i18n.get("forms.abortTransaction.link")}
                        text={i18n.get("forms.abortTransaction.link")}
                    />
                )}
                {st === "PENDING" && (
                    <Button
                        icon="WritingPending"
                        iconVariant="outline"
                        variant="solid"
                        onClick={() => {
                            const idTrx = trx?.idTransaction;
                            dispatch(push(`/transaction/${idTrx}`));
                        }}
                        isInactive={false}
                        loading={false}
                        aria-label={i18n.get("forms.signTransaction.link")}
                        text={i18n.get("forms.signTransaction.link")}
                    />
                )}
                <DownloadButton
                    fetching={fetchingDownload}
                    handleClick={handleDownload}
                    buttonClass="btn-outline download text-transform-none font-regular f-size-5"
                    handleClickMessage="global.download"
                    csvDownload
                    xlsDownload
                    imageStyle="mr-2"
                />
            </Box>
        );
    }, [embeddedInV3Shell, directDebitSelected?.transaction, fetchingDownload, dispatch, cancelTransaction]);

    useEmbeddedV3ShellLayoutSync(dispatch, {
        embeddedInV3Shell,
        isDesktop,
        titleKey: "directDebit.title",
        goBackPath: "/v2/directDebit/list",
        scopes: ["directDebit"],
        rightContentMobile: detailShellRightContentMobile,
    });

    const statusTagColor = (state) => {
        switch (state) {
            case "FINISHED":
                return "#ffeac3";
            case "PENDING":
                return "#ffeac3";
            case "PROCESSED_WITH_SUCCESS":
                return "#84EFD0";
            case "PROCESSED_WITH_ERRORS":
                return "#e0e0e0";
            case "CANCELLED":
                return "#feb0af";
            case "PROCESSING":
                return "#84EFD0";
            default:
                return "#e0e0e0";
        }
    };

    const getLabels = (id) => {
        const labels = {
            type: i18n.get("directDebit.payrollsNumber.actionConfirmation.label").toUpperCase(),
            creditAccount: i18n.get("directDebit.creditAccount.actionConfirmation.label").toUpperCase(),
            fileReference: i18n.get("directDebit.reference.actionConfirmation.label").toUpperCase(),
            uploadDate: i18n.get("directDebit.loadDate.actionConfirmation.label").toUpperCase(),
            chargesOrdersCount: i18n.get("directDebit.debitOrders.actionConfirmation.label").toUpperCase(),
            totalAmount: i18n.get("directDebit.totalAmount.actionConfirmation.label").toUpperCase(),
            cbu: i18n.get("directDebit.cbu.detail.label"),
            dueDate: i18n.get("directDebit.dueDate.detail.label"),
            unequivocalReference: i18n.get("directDebit.unequivocalReference.detail.label"),
            reference: i18n.get("directDebit.reference.detail.label"),
            payroll: i18n.get("directDebit.payrollsNumber.actionConfirmation.label").toUpperCase(),
            fileName: i18n.get("directDebit.fileName.actionConfirmation.label").toUpperCase(),
            sendDate: i18n.get("directDebit.sendDate.actionConfirmation.label").toUpperCase(),
            motivation: i18n.get("directDebit.motivation.detail.label"),
            PENDING: i18n.get("directDebit.detail.item.status.PENDING"),
            FINISHED: i18n.get("directDebit.detail.item.status.FINISHED"),
            FAILED: i18n.get("directDebit.detail.item.status.FAILED"),
            CANCELLED: i18n.get("directDebit.detail.item.status.CANCELLED"),
        };
        return labels[id] || id;
    };

    const getTitle = () => {
        const { transaction } = directDebitSelected || {};
        const name = `${i18n.get("directDebit.list.requestId.label")} ${transaction?.idTransaction
            .substring(0, 8)
            .toUpperCase()}`;
        let fileName = "";
        const idTransactionStatus = directDebitSelected?.status;
        fileName =
            transaction?.data?.file?.newFileName
                ?.split(".")
                .slice(0, -1)
                .join(".") || "";

        return (
            <div className="d-flex flex-column">
                <div className="view-title data-wrapper-flex mb-3">
                    <h1 className="my-0 mr-3">{name}</h1>
                    {transaction !== null && (
                        <InfoTag
                            type="info"
                            message={i18n.get(`directDebit.detail.status.${idTransactionStatus}`)}
                            tagBackground={statusTagColor(idTransactionStatus)}
                            moreStyles={{
                                color: "black",
                            }}
                        />
                    )}
                </div>
                <div className="view-title data-wrapper-flex">
                    <span className="">{i18n.get("payments.thirdPayment.detail.fileName")}:</span>
                    <span className="pl-2">{fileName}</span>
                </div>
            </div>
        );
    };

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="directDebit" />}
            <div className={`admin-detail-head ${isDesktop ? "px-5" : "px-2"}`}>
                {isDesktop && (
                    <>
                        <div className="admin-detail-head px-0">
                            <Head onBack={handleBack} />
                            <Head
                                headerClassName="mb-2"
                                isFetchingExport={false}
                                centerElement={getTitle}
                                rightContent={() => (
                                    <Box justify="end" align="center">
                                        {["PENDING", "PROCESSING"].includes(
                                            directDebitSelected?.transaction?.idTransactionStatus,
                                        ) && (
                                            <Button
                                                icon="Error"
                                                iconVariant="outline"
                                                variant="solid"
                                                onClick={() => cancelTransaction(directDebitSelected?.transaction)}
                                                isInactive={false}
                                                loading={false}
                                                aria-label={i18n.get("forms.abortTransaction.link")}
                                                text={i18n.get("forms.abortTransaction.link")}
                                                w="100%"
                                                className="px-6 mr-3"
                                            />
                                        )}
                                        {directDebitSelected?.transaction?.idTransactionStatus === "PENDING" && (
                                            <Button
                                                icon="WritingPending"
                                                iconVariant="outline"
                                                variant="solid"
                                                onClick={() => {
                                                    const idTrx = directDebitSelected?.transaction?.idTransaction;
                                                    dispatch(push(`/transaction/${idTrx}`));
                                                }}
                                                isInactive={false}
                                                loading={false}
                                                aria-label={i18n.get("forms.signTransaction.link")}
                                                text={i18n.get("forms.signTransaction.link")}
                                                w="100%"
                                                className="px-6"
                                            />
                                        )}
                                        <DownloadButton
                                            fetching={fetchingDownload}
                                            handleClick={handleDownload}
                                            buttonClass="btn-outline download text-transform-none font-regular f-size-5"
                                            handleClickMessage="global.download"
                                            csvDownload
                                            xlsDownload
                                            imageStyle="mr-2"
                                        />
                                    </Box>
                                )}
                            />
                        </div>
                    </>
                )}
                {!isDesktop && !embeddedInV3Shell && (
                    <Head
                        accessibilityTextId="directDebit.title"
                        titleClassName={
                            !isDesktop ? "header-mobile-title-background-blue white-space-normal align-center" : ""
                        }
                        headerClassName={
                            !isDesktop ? "blue-main-header-mobile blue-main-title-mobile background-transparent" : ""
                        }
                        centerElement={() => (
                            <Text as="h1" className="m-0">
                                {i18n.get("directDebit.title")}
                            </Text>
                        )}
                        onBack={!isDesktop && handleBack}
                        uniqueDownload
                        isMobile
                        downloadImageWhite
                        isFetchingExport={fetchingDownload}
                        handleClick={handleDownload}
                    />
                )}
                <Box className="pt-4">
                    <DebitDirectDetail
                        type={directDebitSelected?.payRoll?.label}
                        creditAccount={accountName}
                        fileReference={directDebitSelected.fileReference}
                        uploadDate={moment(directDebitSelected.loadDate).format("DD/MM/YYYY HH:mm")}
                        chargesOrdersCount={directDebitSelected.ordersCount}
                        currency={directDebitSelected.currency}
                        amount={directDebitSelected.amount}
                        getLabels={getLabels}
                        data={dataDetails.slice((pageNumber - 1) * ROW_PER_PAGE, pageNumber * ROW_PER_PAGE) || []}
                        loading={false}
                        paginationProps={{
                            totalPages,
                            pageNumber,
                            action: (pn) => setPageNumber(pn),
                        }}
                    />
                </Box>
            </div>
        </>
    );
};

DetailDirectDebit.propTypes = {
    isDesktop: PropTypes.bool.isRequired,
    dispatch: PropTypes.func.isRequired,
    directDebitList: PropTypes.arrayOf(PropTypes.object).isRequired,
    accounts: PropTypes.arrayOf(PropTypes.object).isRequired,
    loggedUser: PropTypes.shape({
        userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    match: PropTypes.shape({
        params: PropTypes.shape({
            idTransaction: PropTypes.string,
        }),
    }).isRequired,
    fetchingDownload: PropTypes.bool.isRequired,
    embeddedInV3Shell: PropTypes.bool,
};

DetailDirectDebit.defaultProps = {
    embeddedInV3Shell: false,
};

const mapStateToProps = (state) => ({
    accounts: accountsSelectors.getAccounts(state),
    loggedUser: sessionSelectors.getUser(state),
    directDebitList: directDebitSelectors.getDirectDebitList(state),
    fetchingDownload: directDebitSelectors.getFetchingDownload(state),
});
export default compose(connect(mapStateToProps), resizableRoute)(DetailDirectDebit);
